import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { datumDE } from '../../lib/zweiFaktor';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { AnleitungDialog } from './AnleitungDialog';
import { EinrichtenDialog, NeuesHandyDialog, NeueCodesDialog, SitzungenDialog } from './SicherheitDialoge';

const Zeile = ({ name, children, testid }) => (
  <div className="flex flex-col gap-1 border-t py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={testid}>
    <span className="text-sm font-medium text-slate-700">{name}</span>
    <div className="text-sm">{children}</div>
  </div>
);

const Aktion = ({ onClick, children, testid }) => (
  <button type="button" onClick={onClick} className="text-left text-blue-700 hover:text-blue-900 hover:underline" data-testid={testid}>{children}</button>
);

const Check = ({ aktiv, onEinrichten }) => (aktiv ? (
  <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4" data-testid="sicherheit-check-gut">
    <span className="text-lg font-bold text-green-700">2/2</span>
    <div>
      <p className="font-semibold text-green-900">Ihr Konto ist gut geschützt</p>
      <p className="text-sm text-green-800">Passwort und Zwei-Faktor-Anmeldung sind eingerichtet.</p>
    </div>
  </div>
) : (
  <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center" data-testid="sicherheit-check-offen">
    <span className="text-lg font-bold text-amber-700">1/2</span>
    <div className="flex-1">
      <p className="font-semibold text-amber-900">Ein Schritt fehlt noch</p>
      <p className="text-sm text-amber-800">Richten Sie die Zwei-Faktor-Anmeldung ein.</p>
    </div>
    <Button type="button" className="bg-blue-800 hover:bg-blue-900" onClick={onEinrichten} data-testid="sicherheit-einrichten">Zwei-Faktor einrichten</Button>
  </div>
));

// Karte „Sicherheit“ im Profil (nur Verwaltungsrollen). Stand kommt nur aus user im Context.
export const SicherheitKarte = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [dialog, setDialog] = useState(null);
  const aktiv = user?.zwei_faktor_aktiv;
  const geladen = aktiv !== undefined;
  const zu = () => setDialog(null);

  useEffect(() => {
    if (params.get('einrichten') !== '1' || !geladen) return;
    if (aktiv === false) setDialog('einrichten');
    const neu = new URLSearchParams(params);
    neu.delete('einrichten');
    setParams(neu, { replace: true });
  }, [params, setParams, geladen, aktiv]);

  return (
    <Card data-testid="sicherheit-karte">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Shield className="h-5 w-5" />Sicherheit</CardTitle>
        <CardDescription>So schützen Sie Ihr Konto.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Solange der 2FA-Stand lädt, fehlen nur die 2FA-Zeilen. */}
        {!geladen ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" aria-label="Wird geladen" data-testid="sicherheit-laedt" />
        ) : (
          <Check aktiv={aktiv} onEinrichten={() => setDialog('einrichten')} />
        )}
        <div>
          {geladen && (
            <Zeile name="Zwei-Faktor-Anmeldung" testid="sicherheit-zeile-2fa">
              {aktiv ? <span className="text-green-700" data-testid="sicherheit-2fa-stand">{user.zwei_faktor_seit ? `Aktiv seit ${datumDE(user.zwei_faktor_seit)}` : 'Aktiv'}</span>
                : <Aktion onClick={() => setDialog('einrichten')} testid="sicherheit-2fa-stand">Aus</Aktion>}
            </Zeile>
          )}
          {aktiv && (
            <>
              <Zeile name="Authenticator-App" testid="sicherheit-zeile-app"><Aktion onClick={() => setDialog('handy')} testid="sicherheit-neues-handy">Neues Handy einrichten</Aktion></Zeile>
              <Zeile name="Wiederherstellungscodes" testid="sicherheit-zeile-codes"><Aktion onClick={() => setDialog('codes')} testid="sicherheit-neue-codes">Neue Codes erzeugen</Aktion></Zeile>
            </>
          )}
          <Zeile name="Andere Geräte" testid="sicherheit-zeile-geraete"><Aktion onClick={() => setDialog('sitzungen')} testid="sicherheit-andere-geraete">Auf allen anderen Geräten abmelden</Aktion></Zeile>
          <Zeile name="Anleitung" testid="sicherheit-zeile-anleitung"><Aktion onClick={() => setDialog('anleitung')} testid="sicherheit-anleitung">Anleitung ansehen</Aktion></Zeile>
        </div>
      </CardContent>
      <EinrichtenDialog open={dialog === 'einrichten'} onClose={zu} />
      <NeuesHandyDialog open={dialog === 'handy'} onClose={zu} />
      <NeueCodesDialog open={dialog === 'codes'} onClose={zu} />
      <SitzungenDialog open={dialog === 'sitzungen'} onClose={zu} />
      <AnleitungDialog open={dialog === 'anleitung'} onOpenChange={(o) => { if (!o) zu(); }} />
    </Card>
  );
};

export default SicherheitKarte;
